<script setup lang="ts">
import {inject, onMounted, ref} from "vue";
import Destination from "@/scripts/components/app/qrcodes/form/destination/Destination.vue";
import QrCodeStyle from "@/scripts/components/app/qrcodes/form/QrCodeStyle.vue";
import ModalCodeType from "@/scripts/components/app/qrcodes/form/destination/ModalCodeType.vue";
import ModalCondition from "@/scripts/components/app/qrcodes/form/destination/ModalCondition.vue";
import QrCodeView from "@/scripts/components/app/qrcodes/form/QrCodeView.vue";
import VPlanRequiredModalAlert from "@/scripts/components/app/common/VPlanRequiredModalAlert.vue";
import {qrCodeFormStore} from "@/scripts/store/qr_code_form.store.js";
import {storeToRefs} from "pinia";
import {useToast} from "vue-toastification";
import {ChevronDownIcon, ChevronUpIcon, PlusIcon, TrashIcon} from "@heroicons/vue/24/solid";
import ModalQrCodeView from "@/scripts/components/app/qrcodes/form/ModalQrCodeView.vue";
import VDownloadCodeButton from "@/scripts/components/app/qrcodes/form/VDownloadCodeButton.vue";

export interface Props {
    identifier: String
}

const props = defineProps<Props>();

const codeFormStore = qrCodeFormStore()

const isStyleBlockOpen = ref(false)
const loading = ref(false)
const isModalOpened = ref(false)
const errors = ref([])

const isPopupCodeOpened = ref(false)

const emitter = inject('emitter')
const toast = useToast()

const {code} = storeToRefs(codeFormStore)

onMounted(() => {
    emitter.on("toggle-qr-condition-modal-save", (data) => {
        if (data.ruleId !== null) {
            codeFormStore.updateDestinationRule(data.destinationId, data.ruleId, data.rule)
        } else {
            codeFormStore.addDestinationRule(data.destinationId, data.rule)
        }
    });

    emitter.on("destination-rule-delete", (destinationId, ruleId) => {
        codeFormStore.deleteDestinationRule(destinationId, ruleId)
    });

    if (props.identifier) {
        codeFormStore.fetchQrCode(props.identifier)
    }

    codeFormStore.$subscribe((mutation, state) => {
        errors.value = []
    })
})

const closeModal = () => {
    isModalOpened.value = false
}

const toggleStyleBlock = () => {
    isStyleBlockOpen.value = !isStyleBlockOpen.value
}

const save = async () => {
    errors.value = []
    loading.value = true

    const errorsList = codeFormStore.validate()

    if (Object.keys(errorsList).length > 0) {
        loading.value = false
        errors.value = errorsList
        return
    }

    try {
        const result = await codeFormStore.saveCode()

        if (result) {
            isPopupCodeOpened.value = true
        }
    } catch (error) {
        if (error.response?.data.code === 'circle_feature_not_allowed') {
            isModalOpened.value = true
        }

        errors.value = error.response.data.errors || []
    }

    loading.value = false
}

const closePopupCode = () => {
    isPopupCodeOpened.value = false
}

const addNewDestination = () => {
    codeFormStore.addDestination({
        type: 'link',
        data: {},
        rules: []
    })
}

const deleteQrCode = async () => {
    if (confirm('Удалить QR код?')) {
        await codeFormStore.deleteByIdentifier(props.identifier)
        toast.success("QR код удален")
        window.location = '/qrcodes'
    }
}

</script>

<template>
    <div class="md:flex" v-if="code">
        <div class="md:w-7/12 w-full">
            <div>
                <destination v-for="(destination, i) in code.destinations" :destination="destination"
                             :destination-id="i"
                             :key="i" class="mb-2"
                             :errors="errors?.['destinations']?.[i] ?? []"
                >

                </destination>

                <button @click="addNewDestination" type="button"
                        class="mt-4 inline-flex border-gray-100 border items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-50">
                    <PlusIcon class="-ml-1 mr-2 h-5 w-5" aria-hidden="true"/>
                    Добавить действие
                </button>

                <div
                    class="border border-gray-100 rounded px-4 py-2 flex justify-between cursor-pointer items-center mt-4 hover:border-indigo-400 hover:border-1"
                    @click="toggleStyleBlock">
                    <div>Настройте внешний вид QR кода</div>
                    <div v-if="isStyleBlockOpen">
                        <ChevronUpIcon class="w-6 h-6" aria-hidden="true"></ChevronUpIcon>
                    </div>
                    <div v-if="!isStyleBlockOpen">
                        <ChevronDownIcon class="w-6 h-6" aria-hidden="true"></ChevronDownIcon>
                    </div>
                </div>

                <div v-if="isStyleBlockOpen" class="px-2 border">
                    <qr-code-style></qr-code-style>
                </div>
            </div>
            <div class="mt-8 flex space-x-2">
                <v-button title="Сохранить" @click="save" :loading="loading"></v-button>
                <button type="button"
                        v-if="identifier"
                        @click="deleteQrCode"
                        class="group text-black inline-flex items-center px-3 py-2 border hover:text-white border-black text-sm leading-4 font-medium rounded-md shadow-sm bg-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                    <TrashIcon class="-ml-1 mr-2 h-3 w-3 text-red-600 group-hover:text-white"
                               aria-hidden="true"></TrashIcon>
                    Удалить
                </button>
            </div>

            <modal-code-type></modal-code-type>
            <modal-condition></modal-condition>
        </div>
        <div class="w-full md:w-5/12 relative flex justify-center">
            <div class="md:fixed">
                <qr-code-view class="mt-10 md:mt-10 w-44 sm:w-60"></qr-code-view>
                <div v-if="code.identifier" class="justify-center mt-2 flex">
                    <v-download-code-button :identifier="identifier"></v-download-code-button>
                </div>
            </div>
        </div>
    </div>
    <v-plan-required-modal-alert :isOpen="isModalOpened" @modal-close="closeModal">
        Чтобы создавать круглые QR коды перейдите на следующий план.
    </v-plan-required-modal-alert>
    <modal-qr-code-view :isOpen="isPopupCodeOpened" @modal-close="closePopupCode" :identifier="code.identifier"></modal-qr-code-view>
</template>
